import type { Brand } from "./types";

// Política PURA de la allowlist de fuentes (Q3), separada de los loaders de
// @remotion/google-fonts (en ./fonts.ts) para poder testearla sin un contexto
// de render: solo nombres + la resolución con fallback. `fonts.ts` tipa su
// registro de loaders como Record<FontName, ...>, así que tsc obliga a que
// haya EXACTAMENTE un loader por cada nombre de esta lista (parity de claves).

export const FONT_NAMES = [
  // Display / heading (condensados y pesados)
  "Anton",
  "Archivo Black",
  "Bebas Neue",
  "Oswald",
  "Fjalla One",
  "Teko",
  "Kanit",
  "League Spartan",
  "Righteous",
  "Bungee",
  "Montserrat",
  "Poppins",
  "Prompt",
  // Cuerpo / sans
  "Inter",
  "Roboto",
  "Lato",
  "Work Sans",
  "DM Sans",
  "Manrope",
  "Sora",
  "Outfit",
  "Nunito",
  "Rubik",
  "Barlow",
  "Raleway",
  // Serif
  "Playfair Display",
  "Merriweather",
  // M2 (docs/hueco-vocabulario.md #8): serifas elegantes para titulares --
  // las 5 verificadas contra node_modules/@remotion/google-fonts instalado
  // (nombre exacto del paquete al final de este archivo, junto a fonts.ts).
  "Lora",
  "Fraunces",
  "Cormorant Garamond",
  "DM Serif Display",
  "Libre Baskerville",
  // Script / display de acento
  "Lobster",
  "Pacifico",
] as const;

export type FontName = (typeof FONT_NAMES)[number];

// Default MEJOR que Poppins/Inter de M1: un display condensado pesado para el
// titular eleva el look de inmediato (el caso de casi todos los proyectos, que
// aún no definen brand.fonts).
export const DEFAULT_HEADING: FontName = "Anton";
export const DEFAULT_BODY: FontName = "Inter";

// M2 (AV-6, docs/hueco-vocabulario.md #3): qué familias tienen un corte de
// ITÁLICA REAL en el paquete @remotion/google-fonts instalado -- la verdad la
// decide node_modules (cada loader expone su propio `Variants` con o sin
// clave "italic" en su .d.ts), no una lista a ojo. `italic: true` sobre una
// capa con una familia SIN itálica real se IGNORA (`familiaTieneItalica`
// abajo) en vez de dejar que el navegador sintetice una cursiva falsa.
//
// Verificado una por una contra el paquete instalado: además de las 11
// nombradas por el plan (Anton, Bebas Neue, Archivo Black, Oswald, Fjalla
// One, Teko, League Spartan, Righteous, Bungee, Lobster, Pacifico) + Inter
// (el plan pedía verificarla -- tampoco tiene), Manrope/Sora/Outfit TAMPOCO
// tienen itálica real en esta versión del paquete: 15 familias sin itálica en
// total, no 12. Documentado en el resumen de cierre del milestone.
export const FONT_ITALIC: Record<FontName, boolean> = {
  // Display / heading
  Anton: false,
  "Archivo Black": false,
  "Bebas Neue": false,
  Oswald: false,
  "Fjalla One": false,
  Teko: false,
  Kanit: true,
  "League Spartan": false,
  Righteous: false,
  Bungee: false,
  Montserrat: true,
  Poppins: true,
  Prompt: true,
  // Cuerpo / sans
  Inter: false,
  Roboto: true,
  Lato: true,
  "Work Sans": true,
  "DM Sans": true,
  Manrope: false,
  Sora: false,
  Outfit: false,
  Nunito: true,
  Rubik: true,
  Barlow: true,
  Raleway: true,
  // Serif
  "Playfair Display": true,
  Merriweather: true,
  Lora: true,
  Fraunces: true,
  "Cormorant Garamond": true,
  "DM Serif Display": true,
  "Libre Baskerville": true,
  // Script / display de acento
  Lobster: false,
  Pacifico: false,
};

// `.includes` sobre el ARRAY (solo valores propios) -- NUNCA `name in objeto`,
// que recorrería la cadena de prototipos y dejaría pasar "__proto__",
// "constructor", "toString", etc. como si fueran fuentes válidas.
const isAllowed = (name: string): name is FontName =>
  (FONT_NAMES as readonly string[]).includes(name);

// Resuelve los NOMBRES de fuente de un documento contra la allowlist. Nombre
// ausente o no reconocido (incluidas claves de Object.prototype) => default.
export const resolveFontNames = (
  brand: Brand | null | undefined,
): { heading: FontName; body: FontName } => {
  const wantH = brand?.fonts?.heading;
  const wantB = brand?.fonts?.body;
  return {
    heading: wantH && isAllowed(wantH) ? wantH : DEFAULT_HEADING,
    body: wantB && isAllowed(wantB) ? wantB : DEFAULT_BODY,
  };
};

// M2 (AV-6): puro y testeable -- `nombre` arbitrario (p. ej. si algún caller
// no pasó antes por resolveFontNames). `.includes` vía `isAllowed` (nunca
// `name in FONT_ITALIC`) por la misma razón de prototype pollution de arriba.
export const familiaTieneItalica = (nombre: string): boolean =>
  isAllowed(nombre) ? FONT_ITALIC[nombre] : false;
